import { useEffect, useState } from "react";
import type { PlatformApiClient, SalonDemoSimulatorCatalog, SalonDemoSimulationDetail } from "../api";
import { PlatformApiError } from "../api";
import "./salons-demo-simulator.css";

function errorText(error: unknown) {
  return error instanceof PlatformApiError ? error.message : "No se pudo ejecutar el turno.";
}

export function SalonsDemoSimulator({ organizationId, api, navigate, basePath }: {
  organizationId: string;
  api: PlatformApiClient;
  navigate: (path: string) => void;
  basePath: string;
}) {
  const [catalog, setCatalog] = useState<SalonDemoSimulatorCatalog | null>(null);
  const [detail, setDetail] = useState<SalonDemoSimulationDetail | null>(null);
  const [customerId, setCustomerId] = useState("cd_cus_demo_martin");
  const [message, setMessage] = useState("");
  const [operatorMessage, setOperatorMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function refresh(conversationId?: string) {
    const nextCatalog = await api.salonDemoSimulatorCatalog(organizationId);
    setCatalog(nextCatalog);
    const id = conversationId ?? detail?.conversationId;
    if (id) setDetail(await api.salonDemoSimulationDetail(organizationId, id));
  }

  useEffect(() => {
    let active = true;
    void api.salonDemoSimulatorCatalog(organizationId).then((data) => {
      if (!active) return;
      setCatalog(data);
      if (data.sessions[0]) {
        void api.salonDemoSimulationDetail(organizationId, String(data.sessions[0].conversation_id))
          .then((item) => { if (active) setDetail(item); })
          .catch((cause: unknown) => { if (active) setError(errorText(cause)); });
      }
    }).catch((cause: unknown) => { if (active) setError(errorText(cause)); });
    return () => { active = false; };
  }, [organizationId, api]);

  async function start() {
    setBusy(true); setError(null);
    try {
      const next = await api.startSalonDemoSimulation(organizationId, customerId);
      setDetail(next);
      setMessage("");
      await refresh(next.conversationId);
    } catch (cause) { setError(errorText(cause)); }
    finally { setBusy(false); }
  }

  async function send(body: string, role: "customer" | "operator") {
    if (!detail || !body.trim()) return;
    setBusy(true); setError(null);
    try {
      const next = await api.turnSalonDemoSimulation(organizationId, detail.conversationId, {
        body: body.trim(), role, idempotencyKey: crypto.randomUUID(),
      });
      setDetail(next);
      if (role === "customer") setMessage("");
      else setOperatorMessage("");
      await refresh(next.conversationId);
    } catch (cause) {
      setError(errorText(cause));
      try { setDetail(await api.salonDemoSimulationDetail(organizationId, detail.conversationId)); } catch {}
    } finally { setBusy(false); }
  }

  async function setControl(mode: "ai" | "human") {
    if (!detail) return;
    setBusy(true); setError(null);
    try {
      const original = await api.salonConversation(organizationId, detail.conversationId);
      await api.mutateSalonConversation(organizationId, detail.conversationId, {
        type: "control", controlMode: mode, expectedRevision: original.operator.revision,
      });
      await refresh(detail.conversationId);
    } catch (cause) { setError(errorText(cause)); }
    finally { setBusy(false); }
  }

  return <section className="cd-sim" aria-labelledby="cd-sim-title">
    <header className="cd-sim__header">
      <div>
        <button type="button" className="cd-sim__back" onClick={() => navigate(basePath + "/conversations")}>← Volver al Inbox</button>
        <h1 id="cd-sim-title">Simular conversación</h1>
        <p>Escribí como cliente, probá el asistente y verificá las operaciones en la misma agenda de Salons.</p>
      </div>
      <span className="cd-sim__badge">{catalog?.mode === "deepseek" ? "DeepSeek configurado" : "Modo guiado · sin proveedor IA"}</span>
    </header>
    <div className="cd-sim__hint">
      <strong>Simulación local.</strong> No se envían mensajes reales por WhatsApp.
      {catalog?.mode === "guided"
        ? " Las respuestas son reglas guiadas, no IA. Configurá DeepSeek en el servidor para probar respuestas inteligentes."
        : " DeepSeek interpreta los mensajes; las reservas siempre se validan en Scheduling."}
    </div>
    <div className="cd-sim__setup">
      <label>Cliente
        <select value={customerId} onChange={(event) => setCustomerId(event.target.value)} disabled={busy}>
          {(catalog?.customers ?? []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
      </label>
      <button type="button" className="cd-sim__primary" disabled={busy || !catalog} onClick={() => void start()}>+ Nueva conversación</button>
      <label>Retomar sesión
        <select value={detail?.conversationId ?? ""} disabled={busy || !catalog?.sessions.length}
          onChange={(event) => { if (event.target.value) void api.salonDemoSimulationDetail(organizationId, event.target.value).then(setDetail).catch((cause: unknown) => setError(errorText(cause))); }}>
          <option value="">Elegí una sesión</option>
          {(catalog?.sessions ?? []).map((item) => <option value={String(item.conversation_id)} key={String(item.conversation_id)}>
            {catalog?.customers.find((c) => c.id === item.customer_id)?.name ?? String(item.customer_id)} · {String(item.phase)}
          </option>)}
        </select>
      </label>
    </div>
    {error && <p role="alert" className="cd-sim__error">{error}</p>}
    {detail ? <div className="cd-sim__workspace">
      <div className="cd-sim__chat">
        <div className="cd-sim__chat-head">
          <strong>Cliente ↔ ClickDialog</strong>
          <span>{detail.controlMode === "human" ? "Control humano" : "Control IA"}</span>
        </div>
        <div className="cd-sim__messages" aria-live="polite">
          {!detail.messages.length && <p className="cd-sim__empty">Probá: “Hola, quiero reservar un corte mañana por la tarde”.</p>}
          {detail.messages.map((item) => <div key={item.id} className={"cd-sim__message " + (item.direction === "outbound" ? "cd-sim__message--agent" : "cd-sim__message--customer")}>
            <small>{item.direction === "inbound" ? "Cliente" : item.sender === "operator" ? "Operador" : item.sender === "guided-demo" ? "Modo guiado" : "Asistente"} · {new Date(item.occurredAt).toLocaleTimeString("es-AR",{hour:"2-digit",minute:"2-digit"})}</small>
            <p>{item.body}</p>
          </div>)}
        </div>
        <form className="cd-sim__composer" onSubmit={(event) => { event.preventDefault(); void send(message, "customer"); }}>
          <label htmlFor="cd-sim-customer-message">Escribir como cliente</label>
          <div>
            <input id="cd-sim-customer-message" value={message} disabled={busy}
              onChange={(event) => setMessage(event.target.value)} placeholder="Quiero un turno mañana por la tarde…" />
            <button type="submit" disabled={busy || !message.trim()}>Enviar</button>
          </div>
        </form>
      </div>
      <aside className="cd-sim__aside">
        <h2>Operación real</h2>
        <p>Sesión: <code>{detail.conversationId}</code></p>
        <dl>
          <dt>Estado</dt><dd>{detail.phase}</dd>
          <dt>Modo</dt><dd>{detail.mode === "deepseek" ? "DeepSeek" : "Guiado (sin IA)"}</dd>
          <dt>Turno confirmado</dt><dd>{detail.appointmentId ?? "Todavía no"}</dd>
        </dl>
        <div className="cd-sim__actions">
          <button type="button" disabled={busy} onClick={() => void setControl("human")}>Tomar control humano</button>
          <button type="button" disabled={busy} onClick={() => void setControl("ai")}>Devolver a IA</button>
        </div>
        {detail.controlMode === "human" && <form className="cd-sim__operator" onSubmit={(event) => { event.preventDefault(); void send(operatorMessage,"operator"); }}>
          <label>Respuesta del operador
            <textarea rows={3} value={operatorMessage} onChange={(event) => setOperatorMessage(event.target.value)} />
          </label>
          <button type="submit" disabled={busy || !operatorMessage.trim()}>Responder como humano</button>
        </form>}
        <div className="cd-sim__links">
          <button type="button" onClick={() => navigate(basePath + "/conversations/" + detail.conversationId)}>Ver en Inbox →</button>
          <button type="button" onClick={() => navigate(basePath + "/customers/" + detail.customerId)}>Customer 360 →</button>
          <button type="button" onClick={() => navigate(basePath + "/agenda")}>Ver Agenda →</button>
          <button type="button" onClick={() => void refresh(detail.conversationId)} disabled={busy}>Actualizar datos</button>
        </div>
      </aside>
    </div> : <p className="cd-sim__empty">Creá una conversación para iniciar una prueba completa.</p>}
  </section>;
}
